<template>
  <OwnSegmentedControl2
    v-if="isDesktop"
    :model-value="activeValue"
    :options="segmentedOptions"
    @update:model-value="onSegmentSelect"
  />

  <OwnDropdown v-else v-model="dropdownModel" :options="dropdownOptions" />
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { type RouteLocationNamedRaw } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'

import { OwnDropdown } from '@/ui/OwnDropdown'
import { OwnSegmentedControl2 } from '@/ui/OwnSegmentedControl'
import { useBreakpoints } from '@/utils/useBreakpoints'

const route = useRoute()
const router = useRouter()
const { isDesktop } = useBreakpoints()

export interface DropdownNavItem {
  label: string
  to: RouteLocationNamedRaw
}

const props = withDefaults(
  defineProps<{
    navItems: DropdownNavItem[]
    transitionBreakpoint?: number
  }>(),
  {
    transitionBreakpoint: 600,
  }
)

const routeLocationAsValue = (to: RouteLocationNamedRaw): string => {
  if (typeof to === 'string') {
    return to
  } else if (to.name && typeof to.name === 'string') {
    return to.name
  }

  throw new Error('Invalid raw location')
}

const segmentedOptions = computed(() => {
  return props.navItems.map(({ to, label }) => ({
    label,
    value: routeLocationAsValue(to),
  }))
})

const activeValue = computed(() => {
  const match = props.navItems.find((item) => {
    const name = routeLocationAsValue(item.to)
    return typeof route.name === 'string' && route.name.startsWith(name)
  })
  return match
    ? routeLocationAsValue(match.to)
    : (segmentedOptions.value[0]?.value ?? '')
})

const onSegmentSelect = (value: string) => {
  const item = props.navItems.find(
    (item) => routeLocationAsValue(item.to) === value
  )
  if (item) {
    router.push(item.to)
  }
}

const dropdownOptions = computed(() => {
  return props.navItems.map(({ label, to }) => ({
    label,
    value: routeLocationAsValue(to),
  }))
})

const dropdownModel = computed({
  get() {
    // @ts-expect-error Vue router upgrade incompat
    return routeLocationAsValue(route)
  },
  set(newValue) {
    const item = props.navItems.find(
      (item) => routeLocationAsValue(item.to) === newValue
    )
    if (item) {
      router.push(item.to)
    }
  },
})
</script>
